import { useEffect, useMemo, useState } from "react";
import { Search, ShieldCheck, UserPlus, UserRound } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/field";

interface AdminUser {
  uid: string;
  email: string;
  display_name?: string;
  photo_url?: string;
  active: boolean;
  system_admin: boolean;
  disabled: boolean;
  current: boolean;
  providers: string[];
  created_at?: string;
  last_sign_in_at?: string;
  created?: boolean;
}

function dateLabel(value?: string) {
  return value ? new Date(value).toLocaleString() : "—";
}

export function UsersAdminPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = () => api<AdminUser[]>("/admin/users").then(setUsers);

  useEffect(() => {
    void load().catch((caught: unknown) =>
      setError(
        caught instanceof Error ? caught.message : "Users could not load.",
      ),
    );
  }, []);

  const filtered = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return users;
    return users.filter((user) =>
      [user.display_name, user.email].some((field) =>
        field?.toLowerCase().includes(value),
      ),
    );
  }, [search, users]);

  const addUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    const data = new FormData(event.currentTarget);
    try {
      const user = await api<AdminUser>("/admin/users", {
        method: "POST",
        body: JSON.stringify({
          email: data.get("email"),
          display_name: data.get("display_name") || undefined,
        }),
      });
      await load();
      setSelected(user);
      setAdding(false);
      setMessage(
        user.created
          ? "User added. They can sign in with Google or an email code."
          : "Existing Firebase user now has Hope Hangar admin access.",
      );
    } catch (caught: unknown) {
      setError(
        caught instanceof Error ? caught.message : "User could not be added.",
      );
    } finally {
      setSaving(false);
    }
  };

  const updateAccess = async (active: boolean) => {
    if (!selected) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const user = await api<AdminUser>(`/admin/users/${selected.uid}`, {
        method: "PATCH",
        body: JSON.stringify({ active }),
      });
      setSelected(user);
      setUsers((all) =>
        all.map((item) => (item.uid === user.uid ? user : item)),
      );
      setConfirmingRemoval(false);
      setMessage(
        active
          ? "Hope Hangar admin access restored."
          : "Hope Hangar admin access removed.",
      );
    } catch (caught: unknown) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Access could not be updated.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="mt-2 text-muted-foreground">
            Manage who can access the Hope Hangar admin console.
          </p>
        </div>
        <Button onClick={() => setAdding((value) => !value)}>
          <UserPlus size={17} />
          Add user
        </Button>
      </div>

      {adding && (
        <Card className="mt-6">
          <CardContent className="p-6">
            <form
              className="grid items-end gap-4 md:grid-cols-[1fr_1fr_auto]"
              onSubmit={addUser}
            >
              <div>
                <Label htmlFor="user-name" className="mb-2 block">
                  Name
                </Label>
                <Input id="user-name" name="display_name" />
              </div>
              <div>
                <Label htmlFor="user-email" className="mb-2 block">
                  Email address
                </Label>
                <Input
                  id="user-email"
                  name="email"
                  type="email"
                  autoComplete="off"
                  required
                />
              </div>
              <Button type="submit" disabled={saving}>
                {saving ? "Adding…" : "Grant admin access"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {(message || error) && (
        <p
          role={error ? "alert" : "status"}
          className={`mt-5 rounded-lg p-3 text-sm ${
            error ? "bg-red-50 text-red-800" : "bg-green-50 text-green-800"
          }`}
        >
          {error || message}
        </p>
      )}

      <div className="relative mt-7 max-w-xl">
        <Search
          className="absolute left-3 top-3 text-muted-foreground"
          size={18}
        />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search users"
          className="pl-10"
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardContent className="divide-y p-0">
            {filtered.map((user) => (
              <button
                key={user.uid}
                type="button"
                onClick={() => {
                  setSelected(user);
                  setConfirmingRemoval(false);
                }}
                className="flex w-full items-center justify-between gap-3 p-4 text-left hover:bg-muted/50"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-secondary text-primary">
                    {user.photo_url ? (
                      <img
                        src={user.photo_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <UserRound size={18} />
                    )}
                  </span>
                  <div className="min-w-0">
                    <strong className="block truncate">
                      {user.display_name || user.email}
                      {user.current && " (you)"}
                    </strong>
                    {user.display_name && (
                      <span className="block truncate text-sm text-muted-foreground">
                        {user.email}
                      </span>
                    )}
                  </div>
                </div>
                <Badge>{user.active ? "Admin" : "Access removed"}</Badge>
              </button>
            ))}
            {!filtered.length && (
              <p className="p-8 text-center text-sm text-muted-foreground">
                {search ? "No users match your search." : "No users yet."}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="h-fit">
          <CardContent className="p-6">
            {selected ? (
              <div className="space-y-5">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={20} />
                    <h2 className="text-xl font-bold">
                      {selected.display_name || selected.email}
                    </h2>
                  </div>
                  <p className="mt-1 break-all text-sm text-muted-foreground">
                    {selected.email}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge>
                    {selected.active ? "Admin access" : "No access"}
                  </Badge>
                  {selected.system_admin && <Badge>System admin</Badge>}
                  {selected.disabled && <Badge>Firebase disabled</Badge>}
                </div>

                <dl className="space-y-3 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Last sign-in</dt>
                    <dd>{dateLabel(selected.last_sign_in_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Added to Firebase</dt>
                    <dd>{dateLabel(selected.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Sign-in providers</dt>
                    <dd>{selected.providers.join(", ") || "Email"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Firebase UID</dt>
                    <dd className="break-all font-mono text-xs">
                      {selected.uid}
                    </dd>
                  </div>
                </dl>

                {selected.disabled && (
                  <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                    This account is disabled at the shared Firebase-project
                    level. Hope Hangar does not change that global status.
                  </p>
                )}

                {selected.active ? (
                  selected.current ? (
                    <p className="text-sm text-muted-foreground">
                      You cannot remove your own administrator access.
                    </p>
                  ) : selected.system_admin ? (
                    <p className="text-sm text-muted-foreground">
                      This user has project-wide system admin access, which is
                      managed outside Hope Hangar.
                    </p>
                  ) : confirmingRemoval ? (
                    <div className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4">
                      <p className="text-sm text-red-900">
                        Remove this user’s Hope Hangar admin access?
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={saving}
                          onClick={() => void updateAccess(false)}
                        >
                          Confirm removal
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={saving}
                          onClick={() => setConfirmingRemoval(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setConfirmingRemoval(true)}
                    >
                      Remove admin access
                    </Button>
                  )
                ) : (
                  <Button
                    disabled={saving || selected.disabled}
                    onClick={() => void updateAccess(true)}
                  >
                    Restore admin access
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Select a user to review their access.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
